import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import FavoriteButton from '../../../components/ui/FavoriteButton';
import { getForecastForEventDate } from '../../../services/weatherService';

const EventCard = ({ event }) => {
  const navigate = useNavigate();
  const [weatherData, setWeatherData] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [showWeather, setShowWeather] = useState(true);
  const [weatherUnavailableReason, setWeatherUnavailableReason] = useState(null);

  // Check if event is in the past
  const isPast = new Date(event?.date) < new Date();

  // Fetch weather data for events within 5 days
  useEffect(() => {
    const fetchWeather = async () => {
      // Check if event has coordinates and date
      if (!event?.latitude || !event?.longitude || !event?.date) {
        setShowWeather(true);
        setWeatherUnavailableReason('no-data');
        return;
      }

      setWeatherLoading(true);

      try {
        const forecast = await getForecastForEventDate(
          event?.latitude,
          event?.longitude,
          event?.date
        );

        // Handle different forecast responses
        if (forecast?.tooFarAway) {
          // Event is more than 5 days away - show message
          setShowWeather(true);
          setWeatherUnavailableReason('too-far');
          setWeatherData(null);
        } else if (forecast?.isPast) {
          // Event is in the past - hide weather
          setShowWeather(false);
          setWeatherData(null);
        } else if (forecast) {
          // Valid forecast data
          setWeatherData(forecast);
          setShowWeather(true);
          setWeatherUnavailableReason(null);
        } else {
          // No data available
          setShowWeather(true);
          setWeatherUnavailableReason('no-data');
          setWeatherData(null);
        }
      } catch (error) {
        console.error('Error fetching weather for event card:', error);
        setShowWeather(true);
        setWeatherUnavailableReason('error');
        setWeatherData(null);
      } finally {
        setWeatherLoading(false);
      }
    };

    fetchWeather();
  }, [event?.latitude, event?.longitude, event?.date]);

  // Helper to clean location string for display
  const formatLocation = (loc) => {
    if (!loc) return '';
    const parts = loc?.split(',');
    // If it's a simple "District, City", return as is
    if (parts?.length <= 2) return loc;
    // If it's long, take the first 2 parts (assuming the new save logic put District/City first)
    // OR if the string is very long (>40 chars), force a slice.
    return parts?.slice(0, 2)?.join(', ');
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      beginner: 'bg-success/10 text-success',
      intermediate: 'bg-warning/10 text-warning',
      advanced: 'bg-error/10 text-error'
    };
    return colors?.[difficulty] || colors?.beginner;
  };

  const getDifficultyLabel = (difficulty) => {
    const labels = {
      beginner: 'Başlangıç',
      intermediate: 'Orta',
      advanced: 'İleri'
    };
    return labels?.[difficulty] || 'Başlangıç';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    // Handle both HH:MM:SS and HH:MM formats
    const timeParts = timeString?.split(':');
    if (timeParts?.length >= 2) {
      return `${timeParts?.[0]}:${timeParts?.[1]}`;
    }
    return timeString;
  };

  // Calculate starting price (minimum of own board and rental prices)
  const getStartingPrice = () => {
    const prices = [];
    if (event?.priceOwnBoard) prices?.push(event?.priceOwnBoard);
    if (event?.priceWithRental) prices?.push(event?.priceWithRental);
    return prices?.length > 0 ? Math.min(...prices) : 0;
  };

  return (
    <div className="bg-card rounded-xl overflow-hidden shadow-organic hover:shadow-organic-lg transition-smooth group flex flex-col h-full">
      {/* Image with lazy loading and progressive display */}
      <div className="relative h-64 md:h-56 lg:h-64 overflow-hidden bg-muted">
        <Image
          src={event?.image}
          alt={event?.imageAlt || `${event?.title} etkinlik görseli`}
          className="w-full h-full object-cover group-hover:scale-105 transition-smooth"
          lazy={true}
        />
        
        {/* Favorite Button - Top Right */}
        <FavoriteButton itemId={event?.id} itemType="event" />
      </div>
      {/* Content */}
      <div className="p-4 md:p-5 lg:p-6 flex flex-col flex-1">
        {/* Title */}
        <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground mb-2 line-clamp-2">
          {event?.title}
        </h3>

        {/* Location & Date */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon name="MapPin" size={16} />
            <span className="text-sm md:text-base truncate block w-full">{formatLocation(event?.location)}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon name="Calendar" size={16} />
            <span className="text-sm md:text-base">
              {formatDate(event?.date)}
            </span>
            {event?.time && (
              <>
                <Icon name="Clock" size={16} className="ml-2" />
                <span className="text-sm md:text-base">
                  {formatTime(event?.time)}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Weather Info with Difficulty Level - Fixed Height Container */}
        {/* Show Weather Preview for Future Events OR Completed Status for Past Events */}

        {/* Bottom Elements Wrapper - pushed to bottom by mt-auto */}
        <div className="mt-auto flex flex-col gap-4">
          {/* Status/Info Badge Row */}
          <div className="flex items-center gap-4 p-3 bg-muted rounded-lg min-h-[60px]">
            {/* Difficulty Badge - Always Visible */}
            <div className="flex-shrink-0">
              <span className={`px-3 py-1 rounded-full text-xs md:text-sm font-medium ${getDifficultyColor(event?.difficulty)}`}>
                {getDifficultyLabel(event?.difficulty)}
              </span>
            </div>
            
            {isPast ? (
              // Past Event - Show Completed Status
              <div className="flex items-center justify-center gap-2 flex-1 py-2">
                <Icon name="CheckCircle" size={16} className="text-gray-500" />
                <span className="text-sm text-gray-500">Etkinlik Tamamlandı</span>
              </div>
            ) : showWeather ? (
              // Future Event - Show Weather Preview
              <>
                {weatherLoading ? (
                  // Loading State
                  <div className="flex items-center gap-2 w-full justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                    <span className="text-sm text-muted-foreground">Yükleniyor...</span>
                  </div>
                ) : weatherData ? (
                  // Success State - Show actual weather data
                  <>
                    {/* Wind Speed */}
                    <div className="flex items-center gap-2">
                      <Icon name="Wind" size={18} className="text-secondary" />
                      <span className="text-sm md:text-base font-medium">{weatherData?.windSpeed} km/h</span>
                    </div>
                    {/* Temperature with Weather Icon */}
                    <div className="flex items-center gap-2">
                      <Icon name={weatherData?.condition?.iconName || 'Cloud'} size={18} className="text-accent" />
                      <span className="text-sm md:text-base font-medium">{weatherData?.temperature?.max}°C</span>
                    </div>
                  </>
                ) : weatherUnavailableReason === 'too-far' ? (
                  // Too Far Away - Show Message
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="Calendar" size={16} className="text-muted-foreground" />
                    <span>Hava durumu 5 gün öncesinde görüntülenecek</span>
                  </div>
                ) : (
                  // No Data State
                  <div className="text-sm text-muted-foreground text-center w-full">Tahmin Yakında</div>
                )}
              </>
            ) : null}
          </div>

          {/* Attendees & Price */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-primary bg-primary/10 px-2.5 py-1 rounded-full">
                Sınırlı Kontenjan{event?.maxParticipants || event?.max_participants ? ` • ${event?.maxParticipants || event?.max_participants} Kişi` : ''}
              </span>
            </div>
            <div className="text-right">
              <span className="text-xl md:text-2xl font-bold text-primary">₺{getStartingPrice()}</span>
              <span className="text-sm text-muted-foreground block">başlangıç fiyatı</span>
            </div>
          </div>

          {/* Action Button */}
          <Button
            variant="default"
            fullWidth
            iconName="ArrowRight"
            iconPosition="right"
            onClick={() => navigate(`/event-details/${event?.id}`)}
          >
            Detayları Gör
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EventCard;